import { useCallback, useEffect, useRef, useState } from "react";

export const STATUS = {
  smooth: { label: "Smooth", dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-950/40", ring: "ring-emerald-200/80 dark:ring-emerald-800/60", hex: "#10b981" },
  quiet: { label: "Light", dot: "bg-sky-400", text: "text-sky-700 dark:text-sky-300", bg: "bg-sky-50 dark:bg-sky-950/40", ring: "ring-sky-200/80 dark:ring-sky-800/60", hex: "#38bdf8" },
  slow: { label: "Slow", dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-950/40", ring: "ring-amber-200/80 dark:ring-amber-800/60", hex: "#f59e0b" },
  congested: { label: "Congested", dot: "bg-rose-500", text: "text-rose-700 dark:text-rose-400", bg: "bg-rose-50 dark:bg-rose-950/40", ring: "ring-rose-200/80 dark:ring-rose-800/60", hex: "#f43f5e" },
  offline: { label: "Offline", dot: "bg-slate-400", text: "text-slate-600 dark:text-slate-400", bg: "bg-slate-100 dark:bg-slate-800/60", ring: "ring-slate-200 dark:ring-slate-700", hex: "#94a3b8" },
  nodata: { label: "No data", dot: "bg-slate-300", text: "text-slate-500", bg: "bg-slate-50 dark:bg-slate-800/40", ring: "ring-slate-200 dark:ring-slate-700", hex: "#cbd5e1" },
};
export const INCIDENT = {
  wrong_way: { label: "Wrong-way", cls: "bg-red-600 text-white" },
  stopped: { label: "Stopped vehicle", cls: "bg-amber-500 text-white" },
  slow_down: { label: "Slow-down", cls: "bg-orange-500 text-white" },
  other: { label: "Other", cls: "bg-slate-500 text-white" },
};
export const ALERT_KIND = {
  radar_offline: "Radar offline", radar_online: "Radar back online", radar_nodet: "Radar not detecting", feed_offline: "Data feed offline", congestion: "Congestion", wrong_way: "Wrong-way vehicle", stopped: "Stopped vehicle",
};
export const CLASS_COLORS = { small: "#1428A0", medium: "#3b82f6", large: "#E3120B" };
export const BRAND = { blue: "#1428A0", red: "#E3120B" };

export const pct = (a, b) => (b ? Math.round(((a - b) / b) * 100) : null);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Malaysian day-first date labels from ISO strings ("2026-11-17" → "17 Nov"), no timezone conversion.
export const fmtDM = (iso) => (iso ? `${iso.slice(8, 10)} ${MONTHS[Number(iso.slice(5, 7)) - 1]}` : "—");
export const fmtDMY = (iso) => (iso ? `${fmtDM(iso)} ${iso.slice(0, 4)}` : "—");
export const fmtDMHM = (iso) => (iso ? `${fmtDM(iso)} ${iso.slice(11, 16)}` : "—");
export const fmtK = (n) => (n == null ? "—" : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 10000 ? `${(n / 1000).toFixed(1)}k` : Number(n).toLocaleString());
export const hhmm = (iso) => (iso ? iso.slice(11, 16) : "—");
export const ago = (sec) => (sec == null ? "—" : sec < 60 ? `${sec}s ago` : sec < 3600 ? `${Math.floor(sec / 60)}m ago` : `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m ago`);

// Airline-cabin style two-tone chime (C5 then E5) through the Web Audio API — no audio files needed.
let ctx;
export function playChime(volume = 0.6) {
  try {
    ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume();
    const t0 = ctx.currentTime;
    [[523.25, 0], [659.25, 0.42]].forEach(([f, dt]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t0 + dt);
      g.gain.exponentialRampToValueAtTime(volume * 0.5, t0 + dt + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 1.3);
      o.connect(g).connect(ctx.destination);
      o.start(t0 + dt);
      o.stop(t0 + dt + 1.4);
    });
  } catch (e) {
    /* audio not available */
  }
}

const SOUND_EVT = "lpt2-sound-change";
export function useSoundPref() {
  const [enabled, setEnabled] = useState(() => localStorage.getItem("lpt2-sound") !== "off");
  useEffect(() => {
    const sync = () => setEnabled(localStorage.getItem("lpt2-sound") !== "off");
    window.addEventListener(SOUND_EVT, sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener(SOUND_EVT, sync); window.removeEventListener("storage", sync); };
  }, []);
  const toggle = useCallback(() => {
    localStorage.setItem("lpt2-sound", localStorage.getItem("lpt2-sound") === "off" ? "on" : "off");
    window.dispatchEvent(new Event(SOUND_EVT));
  }, []);
  return { enabled, toggle };
}

// Display helpers pinned to Malaysia time so the UI never mixes browser-local and MYT clocks.
const MYT = "Asia/Kuala_Lumpur";
export const fmtTimeMYT = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: MYT }) : "—");
export const fmtDateTimeMYT = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: MYT }) : "—");

// Chime once for every alert id that has not been seen in this browser session.
export function useAlertChime(alerts, enabled, volume = 0.6) {
  const seen = useRef(new Set(JSON.parse(sessionStorage.getItem("lpt2-seen-alerts") || "[]")));
  const first = useRef(true);
  useEffect(() => {
    if (!alerts) return;
    const fresh = alerts.filter((a) => !seen.current.has(a.id) && !a.acked_at);
    alerts.forEach((a) => seen.current.add(a.id));
    sessionStorage.setItem("lpt2-seen-alerts", JSON.stringify([...seen.current].slice(-500)));
    if (fresh.length && enabled && !first.current) playChime(volume);
    first.current = false;
  }, [alerts, enabled, volume]);
}

export function downloadUrl(path, params) {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== "")).toString();
  return `${process.env.REACT_APP_BACKEND_URL}/api${path}?${q}`;
}
